/**
 * LeafletMap Component
 * 
 * A reusable React component to display an interactive Leaflet map.
 */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { initializeMap, setMapView, fitMapToBounds } from '../../utils/mapUtils'; // Adjust path as needed
import './LeafletMap.css'; // Optional: Add custom CSS for the map container

const LeafletMap = ({
  mapId = 'leaflet-map', // Default ID for the map container
  center = [39.8283, -98.5795], // Default center (US)
  zoom = 4, // Default zoom level
  tileProvider = 'esri', // Default tile provider — see mapUtils.js for why OSM's and CARTO's free tiers don't work here
  mapOptions = {}, // Additional Leaflet map options
  className = '', // Custom CSS class for the container
  style = {}, // Custom inline styles for the container
  whenCreated, // Callback function when the map instance is created: (map) => void
  children // Allow rendering children components over the map (e.g., markers, popups)
}) => {
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null); // Store the map instance
  const [isMapInitialized, setIsMapInitialized] = useState(false);

  useEffect(() => {
    // Initialize map only once when the component mounts and the container ref is available
    if (mapContainerRef.current && !mapInstanceRef.current) {
      console.log(`Initializing map on container: ${mapId}`);
      try {
        const map = initializeMap(mapId, { center, zoom, ...mapOptions }, tileProvider);
        mapInstanceRef.current = map;
        setIsMapInitialized(true);

        // Call the callback function if provided
        if (whenCreated) {
          whenCreated(map);
        }

      } catch (error) {
        console.error("Error initializing Leaflet map:", error);
        // Handle initialization error (e.g., show an error message)
      }
    }

    // Cleanup function: Destroy the map instance when the component unmounts
    return () => {
      if (mapInstanceRef.current) {
        console.log(`Destroying map instance: ${mapId}`);
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        setIsMapInitialized(false);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapId]); // Re-run only if mapId changes (should be stable)

  // Effect to update map view when center or zoom props change
  useEffect(() => {
    if (mapInstanceRef.current && isMapInitialized) {
      const currentCenter = mapInstanceRef.current.getCenter();
      const currentZoom = mapInstanceRef.current.getZoom();

      // Check if props have actually changed to avoid unnecessary updates
      if (currentCenter.lat !== center[0] || currentCenter.lng !== center[1] || currentZoom !== zoom) {
         console.log(`Updating map view - Center: ${center}, Zoom: ${zoom}`);
         setMapView(mapInstanceRef.current, center, zoom, { animate: true }); // Animate view changes
      }
    }
  }, [center, zoom, isMapInitialized]);


  // --- Optional: Add functions to expose map instance or common actions ---
  // Example: Function to fit bounds programmatically
  const fitBounds = (target, options) => {
    if (mapInstanceRef.current && isMapInitialized) {
      fitMapToBounds(mapInstanceRef.current, target, options);
    }
  };

  // Example: Expose map instance via a ref (alternative to whenCreated callback)
  // useImperativeHandle(ref, () => ({
  //   getMap: () => mapInstanceRef.current,
  //   fitBounds: fitBounds,
  // }));


  return (
    <div 
      id={mapId} 
      ref={mapContainerRef} 
      className={`leaflet-map-container ${className}`} // Combine default and custom classes
      style={{ height: '400px', width: '100%', ...style }} // Default size, can be overridden by style prop
    >
      {/* Render children only after map is initialized, passing the map instance */}
      {isMapInitialized && typeof children === 'function' 
        ? children(mapInstanceRef.current) 
        : children}
    </div>
  );
};

LeafletMap.propTypes = {
  mapId: PropTypes.string,
  center: PropTypes.arrayOf(PropTypes.number),
  zoom: PropTypes.number,
  tileProvider: PropTypes.string,
  mapOptions: PropTypes.object,
  className: PropTypes.string,
  style: PropTypes.object,
  whenCreated: PropTypes.func,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]), // Allow function as child for passing map instance
};

export default LeafletMap;

// --- Optional CSS (src/components/common/LeafletMap.css) ---
/*
.leaflet-map-container {
  background-color: #f0f0f0; // Placeholder background while map loads
  border: 1px solid #ccc;
  border-radius: 4px;
  overflow: hidden; // Ensure map tiles stay within bounds
}

// Ensure Leaflet controls render correctly
.leaflet-control-container .leaflet-control {
  margin-bottom: 10px !important; // Example adjustment
} 
*/